import type { FocusModeSetting } from '@/features/preferences'

/**
 * Focus mode, the browser half. Mirrors the backend's notify/focus.ts: the same categories decide
 * which notifications may pop up (desktop, Dynamic Island, sounds) while focusing. The bell
 * always keeps everything; focus only decides what interrupts.
 */

export const FOCUS_CATEGORIES = [
  { id: 'mentions', label: 'Mentions', detail: 'Someone @mentions you' },
  { id: 'approvals', label: 'Approvals', detail: 'Asked to approve, approved, sent back, extensions' },
  { id: 'assignments', label: 'Work assigned to you', detail: 'New work on your plate' },
  { id: 'support', label: 'Support tickets assigned', detail: 'L3 tickets assigned to you or your team' },
  { id: 'due', label: 'Due & overdue', detail: 'Due soon, overdue, missed reports, ETA changes' },
  { id: 'meetings', label: 'Meeting reminders', detail: 'From your Google Calendar' },
  { id: 'updates', label: 'Status updates', detail: 'Completed, blocked, moved' },
  { id: 'new', label: 'New clients, projects & requests', detail: 'Org-wide announcements' },
] as const

export type FocusCategory = (typeof FOCUS_CATEGORIES)[number]['id']

export const DEFAULT_FOCUS: FocusModeSetting = {
  enabled: false,
  allow: ['mentions', 'support'],
  external: false,
  until: null,
  supportL3: { enabled: false, teams: [], priorities: [], clientClasses: [], assignedTo: [], search: '' },
}

/** Category of a notification kind; null for the L3 watch, which always gets through. */
export function focusCategory(kind: string): FocusCategory | null {
  if (kind === 'support_l3_watch') return null
  if (kind === 'mention') return 'mentions'
  if (kind.startsWith('approval') || kind === 'approved' || kind === 'sent_back' || kind.startsWith('extension_')) return 'approvals'
  if (kind === 'support_assigned' || kind === 'support_team_assigned') return 'support'
  if (kind === 'assigned' || kind === 'new_work') return 'assignments'
  if (kind.startsWith('due_in_') || kind.startsWith('overdue_') || kind.startsWith('report_') || kind === 'eta_changed') return 'due'
  if (kind === 'new_client' || kind === 'new_project' || kind === 'new_request') return 'new'
  return 'updates'
}

export function focusActive(focus: FocusModeSetting | null | undefined, now = Date.now()): focus is FocusModeSetting {
  return Boolean(focus?.enabled && (!focus.until || Date.parse(focus.until) > now))
}

/** Whether something in this category may interrupt right now. */
export function focusAllowsCategory(focus: FocusModeSetting | null | undefined, category: FocusCategory | null, now = Date.now()): boolean {
  if (!focusActive(focus, now) || category === null) return true
  return focus.allow.includes(category)
}

export const focusAllowsKind = (focus: FocusModeSetting | null | undefined, kind: string, now = Date.now()) =>
  focusAllowsCategory(focus, focusCategory(kind), now)

/** "Until 6:30 PM", "Until tomorrow 9:00 AM", or "Until you turn it off". */
export function untilLabel(until: string | null): string {
  if (!until) return 'Until you turn it off'
  const d = new Date(until)
  const sameDay = d.toDateString() === new Date().toDateString()
  return `Until ${sameDay ? '' : `${d.toLocaleDateString(undefined, { weekday: 'short' })} `}${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`
}
